import { Row } from '@devexpress/dx-react-grid';
import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { convertToDate, useWorkProvidersWithSms, useCombinedNpsSmsEmailBillingReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate } from 'utils/helpers';
import { AxiosError } from 'axios';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import {
  COMBINED_BILLING_REPORT_GRID_TITLE,
  COMBINED_BILLING_REPORT_MESSAGE,
  COMBINED_BILLING_REPORT_TITLE,
  columns,
  summaryColumns
} from './constants';
import { createReportURL } from 'core/routes';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { WorkProviderSearchForm } from 'components/ReportForms/WorkProviderSearchForm';
import { WorkProviderGroup } from 'api/resources/models/AutoGenerated';
import { ReportsLoader } from 'components/ReportsLoader';
import { ReportProps } from 'pages/ReportsDetails/hooks';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import ReportGridNew from 'components/ReportGrid_New';
import { noCSVexportFormats, noExportOption, shortDateFormat, EMPTY_PANEL_TEXT } from 'components/ReportGrid_New/constants';


export function CombinedNpsSmsEmailBillingReport({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    startDate: urlParams.get('startDate'),
    endDate: urlParams.get('endDate'),
    wPGroupNames: urlParams.get('workProvider'),
  };

  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParamsValue.startDate) ?? null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParamsValue.endDate) ?? null
  );
  const [workProvider, setWorkProvider] = useState<string | null>(
    urlParamsValue.wPGroupNames || null
  );
  const { workProviders, isWorkProvidersLoading } = useWorkProvidersWithSms(
    (error) => handleError(error, 'isWorkProvider')
  );
  const options = workProviders?.map((e: WorkProviderGroup, i: number) => {
    return {
      id: i,
      value: e.groupName,
      text: e.workProvider,
    };
  });

  const queryEnabled = !!urlParamsValue.startDate && !!urlParamsValue.endDate && !!urlParamsValue.wPGroupNames;

  const { reportData, refetchReport, isReportFetching } =
    useCombinedNpsSmsEmailBillingReport(
      {
        startDate: urlParamsValue.startDate,
        endDate: urlParamsValue.endDate,
        wPGroupNames: urlParamsValue.wPGroupNames,
      },
      queryEnabled,
      (error: any) => handleError(error, 'isWorkProvider')
    );

  const rows = useMemo<Row>(() => reportData, [reportData]);

  const summaryRows = useMemo<Row>(() => rows?.data?.summary, [rows?.data?.summary]);
  const detailsRows = useMemo<Row>(() => rows?.data?.details, [rows?.data?.details]);

  useEffect(() => {
    navigateToReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlParamsValue.startDate,
    urlParamsValue.endDate,
  ]);

  const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDate(new Date())} for period ${formatDate(startDate)} to ${formatDate(endDate)}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={COMBINED_BILLING_REPORT_TITLE}
          infoMessage={COMBINED_BILLING_REPORT_MESSAGE}
        />
        {isWorkProvidersLoading ? (
          <ReportsLoader loadingRowsCount={2} />
        ) : (
          <WorkProviderSearchForm
            handleSubmit={handleSubmit}
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            isDisabled={isReportFetching}
            workProvider={workProvider}
            setWorkProvider={setWorkProvider}
            options={options}
          />
        )}
      </UpperInfoContainer>
      {!isReportFetching && detailsRows && detailsRows?.length === 0 && <NoReportDataMessage />}
      <div style={{ paddingBottom: 20 }}>
        <ReportGridContainer>
          <ReportGridNew
            allowGrouping={false}
            allowSorting={false}
            columns={summaryColumns}
            displayGroupingPanel
            emptyPanelText={EMPTY_PANEL_TEXT}
            exportFormats={noExportOption}
            isLoading={isReportFetching}
            rows={summaryRows}
            showFiltering={false}
            showPaging={false}
            showSelection={false}
            title={"SummaryByWorkProvider"}
          />
        </ReportGridContainer>
      </div>
      <div>
        <ReportGridContainer direction="column">
          <ReportGridNew
            columns={columns}
            dateFormat={shortDateFormat}
            displayGroupingPanel
            exportFormats={noCSVexportFormats}
            headerTitleForSheet={COMBINED_BILLING_REPORT_TITLE}
            isLoading={isReportFetching}
            pdfOrientation='l'
            reportHeaderTagLine={reportHeaderTagLine}
            rows={detailsRows}
            title={COMBINED_BILLING_REPORT_GRID_TITLE}
          />
        </ReportGridContainer>
      </div>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParamsValue.startDate == dateToString(startDate) &&
      urlParamsValue.endDate == dateToString(endDate) &&
      urlParamsValue.wPGroupNames == workProvider
    )
      refetchReport();
    else navigateToReport();
  }

  function navigateToReport() {
    if (!!startDate && !!endDate && !!workProvider)
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
            workProvider: workProvider,
          },
          reportPath
        ), { replace: true });
  }

  function handleError(
    error: AxiosError,
    type: 'isReport' | 'isWorkProvider',
  ) {
    reportLogger(error);
    setErrorMessage(
      type == 'isReport'
        ? errorMessages.reports('Combined NPS, SMS and Email Billing Report - PPG Internal')
        : errorMessages.workProviderAdded
    );
    setToast(true);
  }
}
